import { logEvent } from "firebase/analytics";
import { getAnalyticsInstance } from "../firebase";

// Simple Analytics for Tech Performance Centre
// Tracks: Page visits, Program page visits, Button clicks

/**
 * Track page visits - Simple and clear
 * @param {string} pageName - Name of the page visited
 * @param {string} pagePath - Path of the page
 */
export const trackPageVisit = (pageName, pagePath) => {
  const analytics = getAnalyticsInstance();

  if (!analytics) {
    console.log("⏳ Analytics not ready yet - Page Visit:", pageName, pagePath);
    return;
  }

  console.log("📊 Page Visit:", pageName, pagePath);

  try {
    logEvent(analytics, "page_visit", {
      page_name: pageName,
      page_path: pagePath,
      visit_time: new Date().toISOString(),
    });
  } catch (error) {
    console.warn("Analytics error:", error);
  }
};

/**
 * Track program page visits - Clear program tracking
 * @param {string} programName - Name of the program
 * @param {string} programPath - Path of the program page
 */
export const trackProgramPageVisit = (programName, programPath) => {
  const analytics = getAnalyticsInstance();

  if (!analytics) {
    console.log(
      "⏳ Analytics not ready yet - Program Page Visit:",
      programName,
      programPath
    );
    return;
  }

  console.log("🎯 Program Page Visit:", programName, programPath);

  try {
    logEvent(analytics, "program_page_visit", {
      program_name: programName,
      program_path: programPath,
      visit_time: new Date().toISOString(),
    });
  } catch (error) {
    console.warn("Analytics error:", error);
  }
};

/**
 * Track button clicks - Simple button tracking
 * @param {string} buttonName - Name/type of button clicked
 * @param {string} programName - Program associated with the button
 * @param {string} location - Where the button is located
 */
export const trackButtonClick = (buttonName, programName, location) => {
  const analytics = getAnalyticsInstance();

  if (!analytics) {
    console.log("⏳ Analytics not ready yet - Button Click:", buttonName);
    return;
  }

  console.log(
    "🔘 Button Click:",
    buttonName,
    "for",
    programName,
    "at",
    location
  );

  try {
    logEvent(analytics, "button_click", {
      button_name: buttonName,
      program_name: programName,
      location: location,
      click_time: new Date().toISOString(),
    });
  } catch (error) {
    console.warn("Analytics error:", error);
  }
};

// Simple program-specific tracking functions
export const trackProgramSpecificEvents = {
  aiTools: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "AI Tools", "header"),
    applyNow: () => trackButtonClick("Apply Now", "AI Tools", "header"),
  },
  softwareEngineering: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "Software Engineering", "header"),
    applyNow: () =>
      trackButtonClick("Apply Now", "Software Engineering", "header"),
  },
  fullStackDeveloper: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "Full Stack Developer", "header"),
    applyNow: () =>
      trackButtonClick(
        "Sign Up for Free Trial",
        "Full Stack Developer",
        "header"
      ),
  },
  cyberSecurity: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "Cyber Security", "header"),
    applyNow: () => trackButtonClick("Apply Now", "Cyber Security", "header"),
  },
  dataScience: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "Data Science", "header"),
    applyNow: () =>
      trackButtonClick("Sign Up for Free Trial", "Data Science", "header"),
  },
  aiMastery: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "AI Mastery", "header"),
    applyNow: () => trackButtonClick("Apply Now", "AI Mastery", "header"),
  },
  aiDev: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "AI Dev", "header"),
    applyNow: () =>
      trackButtonClick("Sign Up for Free Trial", "AI Dev", "header"),
  },
  aiBusiness: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "AI Business", "header"),
    applyNow: () =>
      trackButtonClick("Sign Up for Free Trial", "AI Business", "header"),
  },
  aiToolsBC: {
    downloadBrochure: () =>
      trackButtonClick("Download Brochure", "AI Tools BC", "header"),
    applyNow: () =>
      trackButtonClick("Sign Up for Free Trial", "AI Tools BC", "header"),
  },
};

// Export all functions
const analyticsExports = {
  trackPageVisit,
  trackProgramPageVisit,
  trackButtonClick,
  trackProgramSpecificEvents,
};

export default analyticsExports;
